// SUMMARY: Typed REST client for the Jarvis gateway API.

import type { ChangeEventDTO, ProjectEntry, TerminalDTO, TurnDTO, WorkspaceDTO } from '@/types/jarvis'

// ===============================
// SECTION: TRANSPORT
// ===============================

async function request<T>(path: string, init?: RequestInit): Promise<T> {
	const response = await fetch(path, {
		...init,
		headers: { 'Content-Type': 'application/json', ...init?.headers },
	})

	if (response.status === 401) throw new UnauthorizedError()

	if (!response.ok) {
		const detail = await response.json().catch(() => ({}))
		throw new Error((detail as { error?: string }).error || 'Anfrage fehlgeschlagen')
	}

	if (response.status === 204) return undefined as T

	return (await response.json()) as T
}

export class UnauthorizedError extends Error {
	constructor() {
		super('Nicht angemeldet')
		this.name = 'UnauthorizedError'
	}
}

function post<T>(path: string, body?: unknown): Promise<T> {
	return request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
}

function patch<T>(path: string, body: unknown): Promise<T> {
	return request<T>(path, { method: 'PATCH', body: JSON.stringify(body) })
}

// ===============================
// SECTION: ENDPOINTS
// ===============================

export const api = {
	login: (password: string) => post<{ ok: true }>('/api/auth/login', { password }),
	logout: () => post<{ ok: true }>('/api/auth/logout'),
	session: () => request<{ authenticated: boolean }>('/api/auth/session'),

	projects: () => request<{ projects: ProjectEntry[] }>('/api/projects').then((result) => result.projects),

	listWorkspaces: () =>
		request<{ workspaces: WorkspaceDTO[] }>('/api/workspaces').then((result) => result.workspaces),
	createWorkspace: (name?: string) =>
		post<{ workspace: WorkspaceDTO }>('/api/workspaces', { name }).then((result) => result.workspace),
	updateWorkspace: (id: string, changes: { name?: string; layout?: string; position?: number }) =>
		patch<{ workspace: WorkspaceDTO }>(`/api/workspaces/${id}`, changes).then((result) => result.workspace),
	deleteWorkspace: (id: string) => request<void>(`/api/workspaces/${id}`, { method: 'DELETE' }),

	createTerminal: (workspaceId: string, input: { projectPath: string; title?: string; model?: string }) =>
		post<{ terminal: TerminalDTO }>(`/api/workspaces/${workspaceId}/terminals`, input).then(
			(result) => result.terminal,
		),
	updateTerminal: (id: string, changes: { title?: string; position?: number; workspaceId?: string }) =>
		patch<{ terminal: TerminalDTO }>(`/api/terminals/${id}`, changes).then((result) => result.terminal),
	deleteTerminal: (id: string) => request<void>(`/api/terminals/${id}`, { method: 'DELETE' }),
	restartTerminal: (id: string, fresh: boolean) => post<{ ok: true }>(`/api/terminals/${id}/restart`, { fresh }),
	interruptTerminal: (id: string) => post<{ ok: true }>(`/api/terminals/${id}/interrupt`),

	listChanges: (params: HistoryQuery) =>
		request<{ changes: ChangeEventDTO[] }>(`/api/history?${buildQuery(params)}`).then((result) => result.changes),
	getChange: (id: string) =>
		request<{ change: ChangeEventDTO }>(`/api/history/${id}`).then((result) => result.change),
	listTurns: (params: HistoryQuery) =>
		request<{ turns: TurnDTO[] }>(`/api/turns?${buildQuery(params)}`).then((result) => result.turns),
	getTurn: (id: string) => request<{ turn: TurnDTO; changes: ChangeEventDTO[] }>(`/api/turns/${id}`),
}

export interface HistoryQuery {
	/** Only entries that changed a file, used by the Änderungen view. */
	files?: 1 | undefined
	terminalId?: string
	workspaceId?: string
	kind?: string
	q?: string
	limit?: number
	offset?: number
}

function buildQuery(params: HistoryQuery): string {
	const search = new URLSearchParams()

	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined && value !== '') search.set(key, String(value))
	}

	return search.toString()
}
